import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GlobalStyles from "../../global/GlobalStyles";
import Breadcrumbs from "../../components/others/BreadCrumbs";
import api from "../../global/api";
import toast from "react-hot-toast";
import logo from "../../logo.png";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Geocoder from "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const EditShipping = () => {
  const { tracking_id } = useParams();
  const [shippingPackage, setShippingPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapType, setMapType] = useState("origin");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    origin: "",
    originLat: "",
    originLng: "",
    destination: "",
    destinationLat: "",
    destinationLng: "",
    sender_name: "",
    sender_email: "",
    sender_contact: "",
    receiver_name: "",
    receiver_email: "",
    receiver_contact: "",
    delivery_duration: "",
    weight: "",
    description: "",
    price: "",
    status: "",
  });

  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const mapContainer = useRef(null);

  const defaultLocation = { lat: 40.7128, lng: -74.006 };

  const breadcrumbItems = [
    { name: "Dashboard", path: "/admin/dashboard" },
    { name: "Manage shipping", path: "/admin/dashboard/manage-shipping" },
    {
      name: "Shipment",
      path: `/admin/dashboard/manage-shipping/${tracking_id}`,
    },
    {
      name: "Edit shipment",
      path: `/admin/dashboard/manage-shipping/${tracking_id}/edit`,
    },
  ];

  const initializeMap = () => {
    if (!mapContainer.current || mapContainer.current._leaflet_map) return;

    const map = L.map(mapContainer.current).setView(
      [defaultLocation.lat, defaultLocation.lng],
      13
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Add search control
    const geocoder = new Geocoder({
      defaultMarkGeocode: false,
      geocoder: new Geocoder.nominatim(),
      position: "topright",
    });

    geocoder.on("markgeocode", function (e) {
      const { center, name } = e.geocode;
      map.setView(center, 13);
      updateMarker(center.lat, center.lng, name);
    });

    geocoder.addTo(map);

    map.on("click", handleMapClick);
    mapInstance.current = map;
  };

  const cleanupMap = () => {
    if (mapInstance.current) {
      mapInstance.current.off();
      mapInstance.current.remove();
      mapInstance.current = null;
    }
    markerInstance.current = null;
  };

  const updateMarker = (lat, lng, address) => {
    if (mapInstance.current) {
      if (markerInstance.current) {
        mapInstance.current.removeLayer(markerInstance.current);
      }
      const marker = L.marker([lat, lng]).addTo(mapInstance.current);
      if (address) {
        marker.bindPopup(address).openPopup();
      }
      markerInstance.current = marker;
    }
  };

  useEffect(() => {
    if (showMap) {
      initializeMap();
    } else {
      cleanupMap();
    }

    return () => {
      cleanupMap();
    };
    // eslint-disable-next-line
  }, [showMap]);

  const getShippingPackage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/track/${tracking_id}`);
      setShippingPackage(response.data);

      // Update form data with fetched shipping package
      setFormData({
        origin: response.data.origin || "",
        originLat: response.data.origin_lat || "",
        originLng: response.data.origin_lng || "",
        destination: response.data.destination || "",
        destinationLat: response.data.destination_lat || "",
        destinationLng: response.data.destination_lng || "",
        sender_name: response.data.sender_name || "",
        sender_email: response.data.sender_email || "",
        sender_contact: response.data.sender_contact || "",
        receiver_name: response.data.receiver_name || "",
        receiver_email: response.data.receiver_email || "",
        receiver_contact: response.data.receiver_contact || "",
        delivery_duration: response.data.delivery_duration || "",
        weight: response.data.weight || "",
        description: response.data.description || "",
        price: response.data.price || "",
        status: response.data.status || "",
      });
    } catch (error) {
      toast.error("Failed to load shipment details");
      navigate("/admin/dashboard/manage-shipping");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getShippingPackage();
    // eslint-disable-next-line
  }, [tracking_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLocationSelect = (type) => {
    setMapType(type);
    setShowMap(true);
    setSearchQuery("");
  };

  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();

      const address = data.display_name || "Selected Location";

      setFormData((prev) => ({
        ...prev,
        [mapType]: address,
        [`${mapType}Lat`]: lat.toString(),
        [`${mapType}Lng`]: lng.toString(),
      }));

      updateMarker(lat, lng, address);
      setShowMap(false);
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}`
      );
      const data = await response.json();

      if (data?.length > 0) {
        const { lat, lon, display_name } = data[0];
        updateMarker(lat, lon, display_name);
        if (mapInstance.current) {
          mapInstance.current.setView([lat, lon], 13);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to find location");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const response = await api.post(
        `/shipment/${shippingPackage.id}/update`,
        {
          origin: formData.origin,
          origin_lat: formData.originLat,
          origin_lng: formData.originLng,
          destination: formData.destination,
          destination_lat: formData.destinationLat,
          destination_lng: formData.destinationLng,
          sender_name: formData.sender_name,
          sender_email: formData.sender_email,
          sender_contact: formData.sender_contact,
          receiver_name: formData.receiver_name,
          receiver_email: formData.receiver_email,
          receiver_contact: formData.receiver_contact,
          delivery_duration: parseInt(formData.delivery_duration),
          weight: parseFloat(formData.weight),
          description: formData.description,
          price: parseFloat(formData.price),
          status: formData.status,
        }
      );

      toast.success(response.data.message || "Shipment updated successfully");
      navigate(`/admin/dashboard/manage-shipping/${tracking_id}`);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update shipping order"
      );
    } finally {
      setFormLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={GlobalStyles.dashbooardPageContainer}>
        <div className={GlobalStyles.dashbooardPageWrap}>
          <div className="w-full flex justify-center items-center h-64">
            <p>Loading shipment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!shippingPackage) {
    return (
      <div className={GlobalStyles.dashbooardPageContainer}>
        <div className={GlobalStyles.dashbooardPageWrap}>
          <div className="w-full flex justify-center items-center h-64">
            <p>Shipment not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.dashbooardPageContainer}>
      <div className={GlobalStyles.dashbooardPageWrap}>
        <div className={GlobalStyles.seperator}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        {showMap && (
          <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-4xl h-3/4 bg-white rounded-lg overflow-hidden relative">
              <div
                ref={mapContainer}
                className="w-full h-full"
                style={{ minHeight: "500px" }}
              ></div>
              <div className="absolute top-4 left-4 right-4 flex gap-2">
                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for a location..."
                    className="flex-1 p-2 rounded shadow-lg"
                  />
                  <button
                    type="submit"
                    className="bg-primary text-white px-4 py-2 rounded"
                  >
                    Search
                  </button>
                </form>
                <button
                  onClick={() => setShowMap(false)}
                  className="bg-white p-2 rounded shadow-lg"
                >
                  Close
                </button>
              </div>
              <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black/50 p-2">
                {searchQuery
                  ? "Press enter to search or click on the map to select location"
                  : "Click on the map to select location"}
              </div>
            </div>
          </div>
        )}

        <div className="w-full flex justify-center">
          <form onSubmit={handleSubmit} className={GlobalStyles.form2}>
            <div className="flex items-center gap-2">
              <img src={logo} alt="" className={GlobalStyles.formLogo} />
              <h2 className={GlobalStyles.formHeader}>
                Edit order - {shippingPackage.tracking_id}
              </h2>
            </div>

            <div className={GlobalStyles.seperator}>
              <input
                type="text"
                name="sender_name"
                placeholder="Sender's name"
                className={GlobalStyles.input}
                value={formData.sender_name}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="sender_contact"
                placeholder="Sender's contact"
                className={GlobalStyles.input}
                value={formData.sender_contact}
                onChange={handleChange}
                required
              />
            </div>

            <input
              type="email"
              name="sender_email"
              placeholder="Sender's email"
              className={GlobalStyles.input}
              value={formData.sender_email}
              onChange={handleChange}
              required
            />

            <div className={GlobalStyles.seperator}>
              <input
                type="text"
                name="receiver_name"
                placeholder="Receiver's name"
                className={GlobalStyles.input}
                value={formData.receiver_name}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="receiver_contact"
                placeholder="Receiver's contact"
                className={GlobalStyles.input}
                value={formData.receiver_contact}
                onChange={handleChange}
                required
              />
            </div>

            <input
              type="email"
              name="receiver_email"
              placeholder="Receiver's email"
              className={GlobalStyles.input}
              value={formData.receiver_email}
              onChange={handleChange}
              required
            />

            <div className="relative">
              <input
                type="text"
                name="origin"
                placeholder="Origin"
                className={GlobalStyles.input}
                value={formData.origin}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                onClick={() => handleLocationSelect("origin")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary text-white px-2 py-1 rounded text-xs"
              >
                Map
              </button>
            </div>

            <div className="relative">
              <input
                type="text"
                name="destination"
                placeholder="Destination"
                className={GlobalStyles.input}
                value={formData.destination}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                onClick={() => handleLocationSelect("destination")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary text-white px-2 py-1 rounded text-xs"
              >
                Map
              </button>
            </div>

            <input
              type="text"
              name="description"
              placeholder="Package description"
              className={GlobalStyles.input}
              value={formData.description}
              onChange={handleChange}
            />

            <div className={GlobalStyles.seperator}>
              <input
                type="number"
                name="delivery_duration"
                placeholder="Package duration (days)"
                className={GlobalStyles.input}
                value={formData.delivery_duration}
                onChange={handleChange}
                required
                min="1"
              />
              <input
                type="number"
                name="weight"
                placeholder="Package weight (lb)"
                className={GlobalStyles.input}
                value={formData.weight}
                onChange={handleChange}
                required
                step="0.01"
                min="0.1"
              />
            </div>

            <input
              type="number"
              name="price"
              placeholder="Shipping cost"
              className={GlobalStyles.input}
              value={formData.price}
              onChange={handleChange}
              required
              step="0.01"
              min="0"
            />

            <select
              className={GlobalStyles.input}
              value={formData.status}
              name="status"
              onChange={handleChange}
            >
              <option value="">Select shipment status</option>
              <option value="pending">Pending</option>
              <option value="in_transit">In transit</option>
              <option value="delivered">Delivered</option>
            </select>

            <button
              type="submit"
              className={GlobalStyles.buttonFull}
              disabled={formLoading}
            >
              {formLoading ? "Updating..." : "Update Order"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditShipping;
